import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { templatesApi } from "../../api/endpoints";
import { recomputeDays } from "../../lib/recomputeDays";
import type { Launch, Template } from "../../types/domain";
import { IcoClose } from "../../components/icons";
import { launchAvatarText } from "../../lib/launchAvatar";

const STAGE_LABELS = { warmup: "Прогрев", sales: "Продажи", close: "Дожим" } as const;

interface Props {
  template: Template;
  launches: Launch[];
  defaultLaunchId: string | null;
  onClose: () => void;
  onApplied: () => void;
}

export function ApplyPopover({ template, launches, defaultLaunchId, onClose, onApplied }: Props) {
  const qc = useQueryClient();
  const ref = useRef<HTMLDivElement | null>(null);

  const eligible = launches.filter((l) => l.status === "active" || l.status === "draft");
  const initialLaunchId =
    (defaultLaunchId && eligible.find((l) => l.id === defaultLaunchId)?.id) || eligible[0]?.id || "";
  const [launchId, setLaunchId] = useState(initialLaunchId);

  const launch = launches.find((l) => l.id === launchId);
  const days = useMemo(() => {
    if (!launch) return [];
    return recomputeDays(launch.days, launch.d1StartDate);
  }, [launch]);

  const preferred = days.filter((d) => d.stage === template.stage);
  const others = days.filter((d) => d.stage !== template.stage);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [onClose]);

  const applyMutation = useMutation({
    mutationFn: ({ dayKey }: { dayKey: string }) =>
      templatesApi.apply(template.id, { launchId, dayKey }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["templates"] });
      qc.invalidateQueries({ queryKey: ["cards", launchId] });
      onApplied();
    },
  });

  return (
    <div className="apply-popover" ref={ref} onClick={(e) => e.stopPropagation()}>
      <div className="apply-popover-head">
        <span>Куда применить</span>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть">
          <IcoClose size={14} />
        </button>
      </div>
      <div className="apply-popover-body">
        <div className="form-row" style={{ marginBottom: 10 }}>
          <label className="form-label" style={{ marginBottom: 4 }}>Запуск</label>
          <select
            className="form-input"
            value={launchId}
            onChange={(e) => setLaunchId(e.target.value)}
          >
            {eligible.length === 0 ? (
              <option value="">Нет активных запусков</option>
            ) : (
              eligible.map((l) => (
                <option key={l.id} value={l.id}>
                  {launchAvatarText(l)} · {l.name}
                </option>
              ))
            )}
          </select>
        </div>
        <div className="form-label" style={{ marginBottom: 4 }}>День</div>
        <div className="apply-days-list">
          {preferred.length > 0 && (
            <>
              <div className="apply-days-group-label">
                Подходит по стадии «{STAGE_LABELS[template.stage]}»
              </div>
              {preferred.map((d) => (
                <button
                  type="button"
                  key={d.id}
                  className="apply-day-row"
                  onClick={() => applyMutation.mutate({ dayKey: d.id })}
                  disabled={applyMutation.isPending}
                >
                  <span className="apply-day-num">D{d.dayNum}</span>
                  <div className="apply-day-meta">
                    <b>{d.stageLabel}</b>
                    <span>
                      {d.weekday}, {d.date}
                    </span>
                  </div>
                  {d.isToday && (
                    <span className="today-badge" style={{ marginLeft: "auto" }}>
                      <span className="live-dot" />
                      Сегодня
                    </span>
                  )}
                </button>
              ))}
            </>
          )}
          {others.length > 0 && (
            <>
              <div className="apply-days-group-label">Остальные дни</div>
              {others.map((d) => (
                <button
                  type="button"
                  key={d.id}
                  className="apply-day-row apply-day-row-muted"
                  onClick={() => applyMutation.mutate({ dayKey: d.id })}
                  disabled={applyMutation.isPending}
                >
                  <span className="apply-day-num">D{d.dayNum}</span>
                  <div className="apply-day-meta">
                    <b>{d.stageLabel}</b>
                    <span>
                      {d.weekday}, {d.date}
                    </span>
                  </div>
                </button>
              ))}
            </>
          )}
          {days.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: "var(--text-3)", textAlign: "center" }}>
              В этом запуске ещё нет дней.
            </div>
          )}
        </div>
        {applyMutation.isError && (
          <div style={{ padding: "8px 12px", fontSize: 12, color: "#DC2626" }}>
            Не удалось применить: {(applyMutation.error as Error).message}
          </div>
        )}
      </div>
    </div>
  );
}
